import React, { useState, useEffect, useMemo } from 'react';
import { giveaway } from '../services/api';

// Returns YYYY-MM string for the current month.
function currentMonth() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// Pretty month label, e.g. "2026-06" -> "June 2026".
function monthLabel(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym || '—';
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const Giveaway = () => {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [monthFilter, setMonthFilter] = useState(''); // '' = all
  const [sourceFilter, setSourceFilter] = useState(''); // '' = all
  const [winner, setWinner] = useState(null);
  const [drawMonth, setDrawMonth] = useState(currentMonth());
  const [drawError, setDrawError] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [showDrawnWinner, setShowDrawnWinner] = useState(null);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await giveaway.list({ limit: 500 });
      setEntries(response.data.entries || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Failed to load giveaway entries:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build a list of months that have at least one entry, sorted newest first.
  const months = useMemo(() => {
    const set = new Set();
    entries.forEach((e) => {
      if (e.created_at) set.add(e.created_at.slice(0, 7));
    });
    return Array.from(set).sort().reverse();
  }, [entries]);

  // Build a list of distinct source values for the filter dropdown.
  const sources = useMemo(() => {
    const set = new Set();
    entries.forEach((e) => { if (e.source) set.add(e.source); });
    return Array.from(set).sort();
  }, [entries]);

  // Apply filters.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (monthFilter && (e.created_at || '').slice(0, 7) !== monthFilter) return false;
      if (sourceFilter && e.source !== sourceFilter) return false;
      if (q) {
        const hay = `${e.name} ${e.company} ${e.email} ${e.industry || ''} ${e.it_needs || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [entries, search, monthFilter, sourceFilter]);

  // Per-month counts for the visible-filtered set.
  const monthCounts = useMemo(() => {
    const counts = {};
    filtered.forEach((e) => {
      const m = (e.created_at || '').slice(0, 7);
      if (m) counts[m] = (counts[m] || 0) + 1;
    });
    return counts;
  }, [filtered]);

  const handleDraw = async () => {
    if (!drawMonth) {
      setDrawError('Pick a month first.');
      return;
    }
    if (!window.confirm(`Draw a winner for ${monthLabel(drawMonth)}? This is irreversible if entries exist.`)) return;
    try {
      setDrawing(true);
      setDrawError(null);
      const response = await giveaway.draw(drawMonth);
      setWinner(response.data.winner);
      setShowDrawnWinner({ winner: response.data.winner, month: response.data.month, alreadyDrawn: response.data.alreadyDrawn });
      await loadEntries();
    } catch (err) {
      setDrawError(err.response?.data?.error || err.message || 'Draw failed');
    } finally {
      setDrawing(false);
    }
  };

  const handleLookupWinner = async (month) => {
    try {
      const response = await giveaway.getWinner(month);
      setShowDrawnWinner({ winner: response.data.winner, month: response.data.month, alreadyDrawn: true });
    } catch (err) {
      if (err.response?.status === 404) {
        setShowDrawnWinner({ winner: null, month, notFound: true });
      } else {
        setDrawError(err.response?.data?.error || err.message);
      }
    }
  };

  const exportCSV = () => {
    const headers = ['id', 'name', 'company', 'email', 'industry', 'it_needs', 'source', 'utm_source', 'utm_medium', 'utm_campaign', 'created_at', 'winner_for_month', 'ip'];
    const rows = filtered.map((e) => headers.map((h) => {
      const v = e[h] == null ? '' : String(e[h]).replace(/"/g, '""');
      return `"${v}"`;
    }).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `giveaway-entries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Giveaway Entries</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={exportCSV} disabled={!filtered.length}>
            Export CSV ({filtered.length})
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 600 }}>{summary.total || 0}</div>
            <div style={{ color: 'var(--text-muted, #888)', fontSize: 13 }}>Total entries</div>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 600 }}>{summary.winners || 0}</div>
            <div style={{ color: 'var(--text-muted, #888)', fontSize: 13 }}>Winners drawn</div>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 600 }}>{summary.source_count || 0}</div>
            <div style={{ color: 'var(--text-muted, #888)', fontSize: 13 }}>Distinct sources</div>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              {summary.first_entry ? summary.first_entry.slice(0, 10) : '—'}
              {' → '}
              {summary.last_entry ? summary.last_entry.slice(0, 10) : '—'}
            </div>
            <div style={{ color: 'var(--text-muted, #888)', fontSize: 13 }}>First → last entry</div>
          </div>
        </div>
      )}

      {/* Draw winner panel */}
      <div className="card" style={{ marginBottom: 16, background: 'var(--bg-elevated, #f9f9f9)' }}>
        <h3 style={{ marginTop: 0 }}>Draw a winner</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Month (YYYY-MM):
            <input
              type="text"
              className="form-input"
              style={{ width: 120 }}
              placeholder="2026-07"
              value={drawMonth}
              onChange={(e) => setDrawMonth(e.target.value)}
              pattern="\d{4}-\d{2}"
            />
          </label>
          <button className="btn btn-primary" onClick={handleDraw} disabled={drawing}>
            {drawing ? 'Drawing…' : `Draw for ${monthLabel(drawMonth)}`}
          </button>
          {months.length > 0 && (
            <select
              className="form-input"
              style={{ width: 'auto' }}
              value=""
              onChange={(e) => { if (e.target.value) handleLookupWinner(e.target.value); }}
            >
              <option value="">Lookup existing winner…</option>
              {months.map((m) => (
                <option key={m} value={m}>{monthLabel(m)}</option>
              ))}
            </select>
          )}
        </div>
        {drawError && (
          <div style={{ color: 'var(--danger, #c0392b)', marginTop: 8 }}>{drawError}</div>
        )}
        {showDrawnWinner && (
          <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--border, #ddd)', borderRadius: 6, background: 'white' }}>
            {showDrawnWinner.notFound ? (
              <p>No winner has been drawn for {monthLabel(showDrawnWinner.month)} yet.</p>
            ) : (
              <>
                <p style={{ margin: 0, color: showDrawnWinner.alreadyDrawn ? '#888' : '#2c7a2c' }}>
                  {showDrawnWinner.alreadyDrawn
                    ? `Winner for ${monthLabel(showDrawnWinner.month)} (already drawn):`
                    : `🎉 Winner for ${monthLabel(showDrawnWinner.month)} drawn!`}
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: 18, fontWeight: 500 }}>
                  {showDrawnWinner.winner.name} — {showDrawnWinner.winner.company}
                </p>
                <p style={{ margin: '2px 0', color: '#666' }}>
                  {showDrawnWinner.winner.email}
                </p>
                {showDrawnWinner.winner.industry && (
                  <p style={{ margin: '2px 0', color: '#666' }}>Industry: {showDrawnWinner.winner.industry}</p>
                )}
                <p style={{ margin: '2px 0', color: '#888', fontSize: 12 }}>
                  Entered: {showDrawnWinner.winner.created_at}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            className="form-input"
            style={{ flex: 1, minWidth: 200 }}
            placeholder="Search name, company, email, industry, needs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="form-input" style={{ width: 'auto' }} value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
            <option value="">All months ({entries.length})</option>
            {months.map((m) => (
              <option key={m} value={m}>{monthLabel(m)} ({monthCounts[m] || 0})</option>
            ))}
          </select>
          <select className="form-input" style={{ width: 'auto' }} value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All sources</option>
            {sources.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {(monthFilter || sourceFilter || search) && (
            <button className="btn" onClick={() => { setSearch(''); setMonthFilter(''); setSourceFilter(''); }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Entries table */}
      {loading ? (
        <p>Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#888' }}>
            {entries.length === 0
              ? 'No entries yet. Share the /giveaway page to get started.'
              : 'No entries match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Name</th>
                <th>Company</th>
                <th>Email</th>
                <th>Industry</th>
                <th>Source</th>
                <th>Winner</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{e.created_at}</td>
                  <td>{e.name}</td>
                  <td>{e.company}</td>
                  <td><a href={`mailto:${e.email}`}>{e.email}</a></td>
                  <td>{e.industry || '—'}</td>
                  <td>
                    {e.source || '—'}
                    {e.utm_source && e.utm_source !== e.source && (
                      <div style={{ fontSize: 11, color: '#888' }}>utm: {e.utm_source}</div>
                    )}
                  </td>
                  <td>
                    {e.winner_for_month
                      ? <span style={{ color: '#2c7a2c', fontWeight: 500 }}>🏆 {e.winner_for_month}</span>
                      : '—'}
                  </td>
                  <td style={{ maxWidth: 280 }}>
                    {e.it_needs
                      ? <span title={e.it_needs}>{e.it_needs.length > 80 ? e.it_needs.slice(0, 80) + '…' : e.it_needs}</span>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Giveaway;
